import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import GlassCard from "@/components/GlassCard";
import { BlurText, SpotlightCard, CountUp } from "@/components/animations";
import api from "@/lib/api";
import { Users, BarChart3, TrendingUp, Trophy } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Cell, ResponsiveContainer, ReferenceLine } from "recharts";
import { TIER_COLORS } from "@/lib/tiers";
import { cohortLabel } from "@/lib/format";


export default function PeerComparison() {
    const [demos, setDemos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        const fetch = async () => {
            try {
                const [p, r, a] = await Promise.all([
                    api.get("/api/score/demo/priya"),
                    api.get("/api/score/demo/rahul"),
                    api.get("/api/score/demo/arjun"),
                ]);
                setDemos([p.data, r.data, a.data]);
            } catch (err) {
                setError(err.response?.data?.detail || "Demo comparison data is temporarily unavailable.");
            }
            setLoading(false);
        };
        fetch();
    }, []);

    const chartData = [
        ...demos.map(d => ({ name: d.name?.split(" ")[0] || "?", score: d.score, tier: d.tier, fill: TIER_COLORS[d.tier] || "#3b82f6" })),
        { name: "Nat. Avg", score: 520, tier: "avg", fill: "#64748b" },
    ];

    const signalKeys = ["academic", "fee_payment", "developer_professional", "social_creator", "coverage_consistency"];
    const signalLabels = { academic: "Academic", fee_payment: "Fee Payment", developer_professional: "Developer", social_creator: "Social", coverage_consistency: "Coverage" };
    const signalColors = ["#3b82f6", "#06b6d4", "#8b5cf6", "#f59e0b", "#10b981"];

    const signalData = signalKeys.map((key, i) => {
        const row = { name: signalLabels[key] };
        demos.forEach(d => { row[d.name?.split(" ")[0] || "?"] = d.signal_scores?.[key]?.score || 0; });
        return row;
    });

    return (
        <div className="pt-24 pb-16 px-6 md:px-12 lg:px-24 min-h-dvh" data-testid="peer-comparison">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <p className="text-xs uppercase tracking-[0.3em] text-cyan-signal font-semibold mb-4">Benchmarking</p>
                <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter text-text-primary mb-4">
                    Peer Comparison
                </h1>
                <p className="text-text-secondary text-base md:text-lg mb-10 leading-relaxed max-w-2xl">
                    See how demo profiles compare across scoring dimensions.
                </p>
            </motion.div>

            {error && !loading && (
                <GlassCard className="p-6 text-center" hover={false}>
                    <p className="text-text-primary text-sm font-medium mb-1">Comparison unavailable</p>
                    <p className="text-text-secondary text-sm">{error}</p>
                </GlassCard>
            )}

            {!loading && !error && demos.length > 0 && (
                <div className="space-y-6">
                    {/* Overall Scores */}
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                        <GlassCard className="p-6" hover={false}>
                            <div className="flex items-center gap-2 mb-5">
                                <Trophy size={18} className="text-cyan-signal" />
                                <h3 className="font-heading text-lg font-medium text-text-primary">Overall Scores</h3>
                            </div>
                            <ResponsiveContainer width="100%" height={240}>
                                <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                    <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 12 }} axisLine={{ stroke: "rgba(255,255,255,0.05)" }} tickLine={false} />
                                    <YAxis domain={[0, 900]} tick={{ fill: "#94a3b8", fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                                    <Tooltip contentStyle={{ background: "#0d1526", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "12px", fontSize: "13px" }} itemStyle={{ color: "#f1f5f9" }} formatter={(v) => [`${v}/900`]} />
                                    <ReferenceLine y={520} stroke="#64748b" strokeDasharray="4 4" />
                                    <Bar dataKey="score" radius={[8, 8, 0, 0]} barSize={48}>
                                        {chartData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </GlassCard>
                    </motion.div>

                    {/* Signal Comparison */}
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                        <GlassCard className="p-6" hover={false}>
                            <div className="flex items-center gap-2 mb-5">
                                <BarChart3 size={18} className="text-purple-soft" />
                                <h3 className="font-heading text-lg font-medium text-text-primary">Signal Breakdown</h3>
                            </div>
                            <ResponsiveContainer width="100%" height={280}>
                                <BarChart data={signalData} layout="vertical" margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                    <XAxis type="number" domain={[0, 100]} tick={{ fill: "#94a3b8", fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                                    <YAxis type="category" dataKey="name" tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} width={80} />
                                    <Tooltip contentStyle={{ background: "#0d1526", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "12px", fontSize: "13px" }} itemStyle={{ color: "#f1f5f9" }} />
                                    {demos.map((d, i) => (
                                        <Bar key={d.name} dataKey={d.name?.split(" ")[0] || "?"} fill={TIER_COLORS[d.tier]} barSize={10} radius={[0, 4, 4, 0]} />
                                    ))}
                                </BarChart>
                            </ResponsiveContainer>
                            <div className="flex gap-4 mt-3 justify-center">
                                {demos.map(d => (
                                    <div key={d.name} className="flex items-center gap-2 text-xs">
                                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: TIER_COLORS[d.tier] }} />
                                        <span className="text-text-secondary">{d.name?.split(" ")[0]}</span>
                                    </div>
                                ))}
                            </div>
                        </GlassCard>
                    </motion.div>

                    {/* Profile Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {demos.map((d, i) => (
                            <motion.div key={d.name} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.1 }}>
                                <GlassCard className="p-5" delay={0}>
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className="w-9 h-9 rounded-full flex items-center justify-center font-heading font-bold text-xs"
                                            style={{ backgroundColor: TIER_COLORS[d.tier] + "20", color: TIER_COLORS[d.tier] }}>
                                            {d.name?.split(" ").map(w => w[0]).join("")}
                                        </div>
                                        <div>
                                            <p className="text-text-primary text-sm font-medium">{d.name}</p>
                                            <p className="text-text-secondary text-xs">{cohortLabel(d.persona_match, "")}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-baseline gap-2">
                                        <span className="font-mono text-2xl font-bold" style={{ color: TIER_COLORS[d.tier] }}>{d.score}</span>
                                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: TIER_COLORS[d.tier] + "20", color: TIER_COLORS[d.tier] }}>{d.tier}</span>
                                    </div>
                                </GlassCard>
                            </motion.div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
